"use client";

import { type ReactNode, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { GasAnswer } from "@/components/tools/chem-physical/gas-answer";
import { GasReference } from "@/components/tools/chem-physical/gas-reference";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  GAS_TERMS,
  GAS_UNIT_OPTIONS,
  type GasFailure,
  type GasTerm,
  idealGas,
} from "@/lib/tool-kit/chem-physical/gas";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";

/** English strings. A translated content file overrides these through `ui`. */
export const IDEAL_GAS_LAW_CALCULATOR_STRINGS = {
  solveLabel: "Solve for",
  solveP: "Pressure (P)",
  solveV: "Volume (V)",
  solveN: "Amount (n)",
  solveT: "Temperature (T)",
  labelP: "Pressure (P)",
  labelV: "Volume (V)",
  labelN: "Amount of gas (n)",
  labelT: "Temperature (T)",
  answerSuffix: "{label} (answer)",
  pressureUnit: "Pressure unit",
  volumeUnit: "Volume unit",
  amountUnit: "Amount unit",
  temperatureUnit: "Temperature unit",
  temperatureHint:
    "Celsius and Fahrenheit are converted to kelvin first — the law needs absolute temperature.",
  presetsLabel: "Fill in a reference condition",
  presetStp: "STP (273.15 K, 1 bar)",
  presetStpLegacy: "Pre-1982 STP (273.15 K, 1 atm)",
  presetSatp: "SATP (298.15 K, 1 bar)",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answerP: "Pressure (P)",
  answerV: "Volume (V)",
  answerN: "Amount of gas (n)",
  answerT: "Temperature (T)",
  molarVolume: "Molar volume (V ÷ n)",
  gasConstant: "Gas constant R used",
  working: "The working",
  equation: "PV = nRT",
  rearrangedP: "P = nRT ÷ V",
  rearrangedV: "V = nRT ÷ P",
  rearrangedN: "n = PV ÷ RT",
  rearrangedT: "T = PV ÷ nR",
  convertLine: "{label}: {value} {unit} = {converted} {target}",
  substitutedP: "P = ({n} mol × {r} J/(mol·K) × {t} K) ÷ {v} m³ = {result} Pa",
  substitutedV: "V = ({n} mol × {r} J/(mol·K) × {t} K) ÷ {p} Pa = {result} m³",
  substitutedN:
    "n = ({p} Pa × {v} m³) ÷ ({r} J/(mol·K) × {t} K) = {result} mol",
  substitutedT:
    "T = ({p} Pa × {v} m³) ÷ ({n} mol × {r} J/(mol·K)) = {result} K",
  answerLine: "{result} {siUnit} = {answer}",
  highPressure:
    "Above about 10 atm, real gases drift noticeably from PV = nRT. Treat this answer as an estimate and use the van der Waals equation for accurate work.",
  enterValues: "Enter the other three quantities to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "Temperature must be above absolute zero: 0 K, −273.15 °C or −459.67 °F. Gas-law temperatures are absolute, so a value at or below that cannot be used.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
  rTableHeading: "The gas constant R in other units",
  rTableNote:
    "R = N_A × k_B is exact since the 2019 SI redefinition. Use the value whose pressure and volume units match yours.",
  columnUnit: "Unit",
  columnValue: "R",
  vmTableHeading: "Molar volume of an ideal gas",
  vmTableNote:
    "IUPAC has defined STP as 273.15 K and 10⁵ Pa since 1982; before that the pressure was 1 atm. SATP is a textbook convention, not an IUPAC term.",
  columnCondition: "Condition",
  columnTemperature: "Temperature",
  columnPressure: "Pressure",
  columnMolarVolume: "V_m (L/mol)",
  conditionStp: "STP (IUPAC, since 1982)",
  conditionStpLegacy: "STP (before 1982)",
  conditionSatp: "SATP (1 bar)",
  conditionSatpLegacy: "SATP (1 atm)",
} as const;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;

interface Entry {
  text: string;
  unit: string;
}

const START: Record<GasTerm, Entry> = {
  p: { text: "1.00", unit: "atm" },
  v: { text: "", unit: "L" },
  n: { text: "2.50", unit: "mol" },
  t: { text: "25.0", unit: "C" },
};

/** Reference conditions and the entries they fill in. */
const PRESETS = [
  { id: "presetStp", t: "273.15", p: "1.00", pUnit: "bar" },
  { id: "presetStpLegacy", t: "273.15", p: "1.00", pUnit: "atm" },
  { id: "presetSatp", t: "298.15", p: "1.00", pUnit: "bar" },
] as const;

function failureMessage(
  failure: GasFailure,
  strings: typeof IDEAL_GAS_LAW_CALCULATOR_STRINGS
): string {
  if (failure.reason === "missing") {
    return strings.enterValues;
  }
  const labels: Record<GasTerm, string> = {
    p: strings.labelP,
    v: strings.labelV,
    n: strings.labelN,
    t: strings.labelT,
  };
  if (failure.reason === "belowZero") {
    return strings.errorBelowZero;
  }
  const template =
    failure.reason === "unreadable"
      ? strings.errorUnreadable
      : strings.errorNonPositive;
  return t(template, { label: labels[failure.term] });
}

export default function IdealGasLawCalculatorWidget() {
  const strings = useToolUi(IDEAL_GAS_LAW_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [unknown, setUnknown] = useState<GasTerm>("v");
  const [entries, setEntries] = useState<Record<GasTerm, Entry>>(START);
  const [sigChoice, setSigChoice] = useState("match");
  const set = (term: GasTerm, patch: Partial<Entry>) =>
    setEntries({ ...entries, [term]: { ...entries[term], ...patch } });

  const texts = {
    p: entries.p.text,
    v: entries.v.text,
    n: entries.n.text,
    t: entries.t.text,
  };
  const units = {
    p: entries.p.unit,
    v: entries.v.unit,
    n: entries.n.unit,
    t: entries.t.unit,
  };
  const outcome = idealGas({ unknown, text: texts, unit: units });
  const typed = GAS_TERMS.filter((term) => term !== unknown).map(
    (term) => texts[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  let body: ReactNode;
  if (outcome.ok) {
    body = (
      <GasAnswer
        sig={sig}
        solution={outcome}
        strings={strings}
        texts={texts}
        units={units}
      />
    );
  } else if (outcome.reason === "missing") {
    body = <Notice>{failureMessage(outcome, strings)}</Notice>;
  } else {
    body = <ErrorNotice>{failureMessage(outcome, strings)}</ErrorNotice>;
  }

  const applyPreset = (preset: (typeof PRESETS)[number]) =>
    setEntries({
      ...entries,
      t: { text: preset.t, unit: "K" },
      p: { text: preset.p, unit: preset.pUnit },
    });

  const labels: Record<GasTerm, string> = {
    p: strings.labelP,
    v: strings.labelV,
    n: strings.labelN,
    t: strings.labelT,
  };
  const unitLabels: Record<GasTerm, string> = {
    p: strings.pressureUnit,
    v: strings.volumeUnit,
    n: strings.amountUnit,
    t: strings.temperatureUnit,
  };
  const field = (term: GasTerm) => (
    <QuantityInput
      hint={term === "t" ? strings.temperatureHint : undefined}
      id={`igl-${term}`}
      key={term}
      label={
        unknown === term
          ? t(strings.answerSuffix, { label: labels[term] })
          : labels[term]
      }
      onUnitChange={(unit) => set(term, { unit })}
      onValueChange={(text) => set(term, { text })}
      solved={unknown === term}
      unit={entries[term].unit}
      unitLabel={unitLabels[term]}
      units={GAS_UNIT_OPTIONS[term]}
      value={entries[term].text}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="igl-solve"
        onChange={setUnknown}
        options={[
          { value: "p", label: strings.solveP },
          { value: "v", label: strings.solveV },
          { value: "n", label: strings.solveN },
          { value: "t", label: strings.solveT },
        ]}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {GAS_TERMS.map(field)}
        <SelectField
          id="igl-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.presetsLabel}</span>
        {PRESETS.map((preset) => (
          <button
            className={BUTTON_SMALL}
            key={preset.id}
            onClick={() => applyPreset(preset)}
            type="button"
          >
            {strings[preset.id]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
      <GasReference strings={strings} />
    </>
  );
}
